import { useCallback } from 'preact/hooks';
import { useDispatch, useSelector } from 'react-redux';

import type { AppDispatch } from '../../store';
import type {
  AuthenticationRequest,
  AuthenticationResult,
  CheckAuthenticationRequest,
} from '../models/auth.model';
import { LoginType } from '../models/auth.model';
import { authenticationResult, login, logout } from '../store/auth.reducer';
import {
  selectAccountType,
  selectAuthentication,
  selectAuthenticationLoading,
  selectCustomer,
  selectCustomerLoading,
  selectLoggedIn,
} from '../store/auth.selectors';
import { checkAccountType, checkAuthentication } from '../store/auth.thunk';

/**
 * Custom hook for authentication state and actions
 * Replaces: AuthFacade
 */
export function useAuth() {
  const dispatch = useDispatch<AppDispatch>();

  // Selectors
  const loggedIn = useSelector(selectLoggedIn);
  const customer = useSelector(selectCustomer);
  const customerLoading = useSelector(selectCustomerLoading);
  const authentication = useSelector(selectAuthentication);
  const authenticationLoading = useSelector(selectAuthenticationLoading);
  const accountType = useSelector(selectAccountType);

  /**
   * Open authentication modal
   * This triggers the auth flow which will open the appropriate modal
   * based on user's authentication state and account type
   */
  const openAuthModal = useCallback(
    (_request?: AuthenticationRequest) => {
      // Check authentication status
      dispatch(
        checkAuthentication({
          loginTypes: [LoginType.CUSTOMER],
        })
      );

      // Check account type (new vs legacy)
      dispatch(checkAccountType());
    },
    [dispatch]
  );

  /**
   * Trigger login action
   */
  const doLogin = useCallback(() => {
    dispatch(login());
  }, [dispatch]);

  /**
   * Trigger logout action
   */
  const doLogout = useCallback(() => {
    dispatch(logout());
  }, [dispatch]);

  /**
   * Manually dispatch authentication result
   * Used when auth flow completes
   */
  const dispatchAuthResult = useCallback(
    (result: AuthenticationResult) => {
      dispatch(authenticationResult(result));
    },
    [dispatch]
  );

  /**
   * Check authentication with specific request
   */
  const checkAuth = useCallback(
    (request: CheckAuthenticationRequest) => {
      dispatch(checkAuthentication(request));
    },
    [dispatch]
  );

  return {
    // State
    loggedIn,
    customer,
    customerLoading,
    authentication,
    authenticationLoading,
    accountType,

    // Actions
    openAuthModal,
    login: doLogin,
    logout: doLogout,
    dispatchAuthResult,
    checkAuth,
  };
}
